import { useCallback, useMemo } from 'react'
import { useWindowDimensions, Platform } from 'react-native'
import DeviceInfo from 'react-native-device-info'

export type ScreenType = 'Small' | 'Medium' | 'Large' | 'Extra-Large'
export type DeviceType = 'Handset' | 'Tablet' | 'Tv' | 'unknown'

export interface UseDimensions {
  screenType: ScreenType
  scale: (value: number, factor?: number) => number
  selectDeviceType: <T>(spec: { [type in ScreenType]?: T }, defaultValue: T) => T
}

// Define base dimensions for different screen types
const BASE_DIMENSIONS: { [key in ScreenType]: { width: number; height: number } } = {
  Small: { width: 350, height: 640 },
  Medium: { width: 600, height: 960 },
  Large: { width: 900, height: 1200 },
  'Extra-Large': { width: 1200, height: 1600 },
}

export const useDimensions = (): UseDimensions => {
  const { width } = useWindowDimensions()
  const screenType: ScreenType = useMemo(() => {
    if (Platform.OS === 'web') {
      if (width < 600) {
        return 'Small'
      } else if (width >= 600 && width < 900) {
        return 'Medium'
      } else if (width >= 900 && width < 1200) {
        return 'Large'
      } else {
        return 'Extra-Large'
      }
    } else {
      const deviceType = DeviceInfo.getDeviceType() as DeviceType
  
      switch (deviceType) {
        case 'Handset':
          return 'Small'
        case 'Tablet':
          return 'Medium'
        case 'Tv':
          return 'Large'
        default:
          return 'Extra-Large'
      }
    }
  },[width])

  // Use screenType to determine the guideline base dimensions
  const { width: guidelineBaseWidth } = BASE_DIMENSIONS[screenType]

  const scale: UseDimensions['scale'] = useCallback((value, factor = 0.4) => {
    return value + ((width / guidelineBaseWidth) * value - value) * factor
  },[width, guidelineBaseWidth])

  const selectDeviceType: UseDimensions['selectDeviceType'] = useCallback((spec, defaultValue) => {
    return spec[screenType] ?? defaultValue
  },[screenType])

  return {
    screenType,
    scale,
    selectDeviceType,
  }
}
